import { getStoredRole } from "../components/api";

const ROLE_ALIASES = {
  ADMIN: ["ADMIN", "ORG_ADMIN", "SUPER_ADMIN", "OWNER"],
  INVENTORY_MANAGER: ["INVENTORY_MANAGER", "INVENTORY_ADMIN", "STORE_MANAGER"],
  PRODUCTION_MANAGER: ["PRODUCTION_MANAGER"],
  PRODUCTION_WORKER: ["PRODUCTION_WORKER", "WORKER"],
  QC_MANAGER: ["QC_MANAGER"],
  QC_INSPECTOR: ["QC_INSPECTOR"],
  HR_MANAGER: ["HR_MANAGER", "HR_ADMIN"],
  HR_STAFF: ["HR_STAFF", "HR_EXECUTIVE"],
  SALES_MANAGER: ["SALES_MANAGER"],
  SALES_STAFF: ["SALES_STAFF"],
  ACCOUNTANT: ["ACCOUNTANT", "ACCOUNTS_MANAGER"],
};

const MODULE_CRUD_MATRIX = {
  admin: {
    read: ["ADMIN"],
    create: ["ADMIN"],
    update: ["ADMIN"],
    delete: ["ADMIN"],
  },
  inventory: {
    read: ["ADMIN", "INVENTORY_MANAGER", "PRODUCTION_MANAGER", "ACCOUNTANT", "PRODUCTION_WORKER"],
    create: ["ADMIN", "INVENTORY_MANAGER"],
    update: ["ADMIN", "INVENTORY_MANAGER"],
    delete: ["ADMIN", "INVENTORY_MANAGER"],
  },
  production: {
    read: ["ADMIN", "PRODUCTION_MANAGER", "PRODUCTION_WORKER"],
    create: ["ADMIN", "PRODUCTION_MANAGER"],
    update: ["ADMIN", "PRODUCTION_MANAGER", "PRODUCTION_WORKER"],
    delete: ["ADMIN", "PRODUCTION_MANAGER"],
  },
  qc: {
    read: ["ADMIN", "QC_MANAGER", "QC_INSPECTOR"],
    create: ["ADMIN", "QC_MANAGER", "QC_INSPECTOR"],
    update: ["ADMIN", "QC_MANAGER", "QC_INSPECTOR"],
    delete: ["ADMIN", "QC_MANAGER"],
  },
  sales: {
    read: ["ADMIN", "SALES_MANAGER", "SALES_STAFF"],
    create: ["ADMIN", "SALES_MANAGER", "SALES_STAFF"],
    update: ["ADMIN", "SALES_MANAGER", "SALES_STAFF"],
    delete: ["ADMIN", "SALES_MANAGER"],
  },
  hr: {
    read: ["ADMIN", "HR_MANAGER", "HR_STAFF"],
    create: ["ADMIN", "HR_MANAGER"],
    update: ["ADMIN", "HR_MANAGER"],
    delete: ["ADMIN", "HR_MANAGER"],
  },
  accounts: {
    read: ["ADMIN", "ACCOUNTANT"],
    create: ["ADMIN", "ACCOUNTANT"],
    update: ["ADMIN", "ACCOUNTANT"],
    delete: ["ADMIN"],
  },
};

function _normalizeRole(value) {
  if (!value) return null;
  return String(value).trim().toUpperCase();
}

function _expandRole(role) {
  const normalized = _normalizeRole(role);
  if (!normalized) return [];
  const hits = Object.entries(ROLE_ALIASES)
    .filter(([, aliases]) => aliases.includes(normalized))
    .map(([canonical]) => canonical);
  return [normalized, ...hits];
}

export function extractUserRoles(user) {
  const roles = new Set();
  const addRole = (value) => {
    _expandRole(value).forEach((r) => roles.add(r));
  };

  const activeRole =
    user?.active_role ||
    getStoredRole() ||
    null;

  addRole(activeRole);

  addRole(user?.role);
  addRole(user?.access_level);
  (user?.roles || []).forEach(addRole);
  (user?.role_options || []).forEach((entry) => {
    addRole(entry?.role);
    addRole(entry?.access_level);
  });

  (user?.access_matrix || []).forEach((entry) => {
    addRole(entry?.role);
    addRole(entry?.access_level);
  });

  addRole(getStoredRole());

  return roles;
}

export function hasAnyRole(user, targetRoles = []) {
  const userRoles = extractUserRoles(user);
  const expandedTargets = new Set();
  targetRoles.forEach((role) => _expandRole(role).forEach((r) => expandedTargets.add(r)));
  return [...expandedTargets].some((r) => userRoles.has(r));
}

export function canModule(user, moduleKey, action = "read") {
  const effectivePermissions = user?.effective_permissions || null;
  const modulePerm = effectivePermissions?.[moduleKey]?.[action];
  if (typeof modulePerm === "boolean") {
    if (modulePerm) return true;
  }
  const matrix = MODULE_CRUD_MATRIX[moduleKey];
  if (!matrix || !matrix[action]) return false;
  return hasAnyRole(user, matrix[action]);
}

export { MODULE_CRUD_MATRIX, ROLE_ALIASES };
